import * as React from "react";
import { t } from "i18next";
import { ImportOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { DropdownChildButton } from "src/components/buttons/DropdownChildButton";
import { MultiStepTask } from "src/components/MultiStepTask";
import { ZUid } from "src/types/zUid";
import { onError } from "src/common/onError";
import { MultiStepItem } from "src/components/MultiStepTask/MultiStepItem";
import {
  lastStep,
  nextStep,
  prevStep,
} from "src/components/MultiStepTask/utils";
import { MultiStepEntsTable } from "src/components/MultiStepTask/utils/MultiStepEntsTable";
import { EntityFiltersTable } from "src/pages/EntityFiltersPage/EntityFiltersTable";
import { Bom2aPosCopyFromStore } from "./Bom2aPosCopyFromStore";
import { Bom2aPosCopyFromStep2 } from "./Bom2aPosCopyFromStep2";
import { Bom2aPosSelectTable } from "../../Bom2aPosSelectTable";

interface PropsBom2aPosCopyFrom {
  modelObjectId: ZUid;
  objectServiceId: ZUid;
  targetBomId: ZUid;
  onFinish: () => void;
}

export const Bom2aPosCopyFrom: React.FC<PropsBom2aPosCopyFrom> = observer(
  (props) => {
    const { modelObjectId, objectServiceId, targetBomId, onFinish } = props;
    const store = React.useMemo(
      () =>
        new Bom2aPosCopyFromStore(modelObjectId, objectServiceId, targetBomId),
      [modelObjectId, objectServiceId, targetBomId],
    );
    const start = () => {
      store.init().catch(onError);
    };

    const steps: MultiStepItem[] = [
      {
        title: t("Model"),
        description: t("Select the source instance to copy"),
        next: nextStep(store.firstDisabled),
        drawContent: () => (
          <MultiStepEntsTable>
            <EntityFiltersTable store={store.store1} />,
          </MultiStepEntsTable>
        ),
      },
      {
        title: "BOM",
        description: t("Select the instance to copy"),
        prev: prevStep(),
        next: nextStep(store.secondDisabled),
        drawContent: () => <Bom2aPosCopyFromStep2 store={store} />,
      },
      {
        title: t("Positions"),
        description: t("Select positions to copy"),
        onActive(fromPrevStep) {
          if (!fromPrevStep) {
            store.onPosActive();
          }
        },
        prev: prevStep(),
        next: nextStep(store.thirdDisabled),
        drawContent: () =>
          store.posStore ? (
            <Bom2aPosSelectTable store={store.posStore} />
          ) : (
            "..."
          ),
      },
      lastStep(t("Copying"), store.copyInfo, () => store.copy(onFinish)),
    ];

    return (
      <>
        <DropdownChildButton icon={<ImportOutlined />} onClick={start}>
          {t("Copy from")}
        </DropdownChildButton>
        <MultiStepTask
          title={t("Copy from")}
          taskStore={store.taskStore}
          steps={steps}
        />
      </>
    );
  },
);
